// src/pages/NetworkAlerts/BroadcastModal.js — disparar aviso do alerta para os clientes do escopo
import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";

import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import CircularProgress from "@material-ui/core/CircularProgress";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import VerifiedUserOutlinedIcon from "@material-ui/icons/VerifiedUserOutlined";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import SendOutlinedIcon from "@material-ui/icons/SendOutlined";

import { StatusBadge } from "../../components/ui";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { useDate } from "../../hooks/useDate";

import useStyles from "./styles";
import { buildAlertText, isOfficialConnection, scopeLabel, toNumber } from "./constants";

/**
 * Props: open, alert, whatsapps (só conexões WhatsApp), onClose, onDone(result).
 */
const BroadcastModal = ({ open, alert, whatsapps, onClose, onDone }) => {
  const classes = useStyles();
  const { datetimeToClient } = useDate();

  const [whatsappId, setWhatsappId] = useState("");
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templateId, setTemplateId] = useState("");
  const [text, setText] = useState("");
  // Test mode: send only to this number (nothing is marked on the alert)
  const [testNumber, setTestNumber] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setWhatsappId(whatsapps.length === 1 ? String(whatsapps[0].id) : "");
    setTemplateId("");
    setTemplates([]);
    setAcknowledged(false);
    setSending(false);
    setResult(null);
    setText(alert ? buildAlertText(alert, datetimeToClient) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, alert, whatsapps]);

  const selected = whatsapps.find((w) => String(w.id) === whatsappId);
  const official = isOfficialConnection(selected);

  useEffect(() => {
    if (!open || !official || !whatsappId) return undefined;
    let active = true;
    setLoadingTemplates(true);
    setTemplateId("");
    api
      .get("/meta-templates", { params: { whatsappId } })
      .then(({ data }) => {
        if (!active) return;
        const list = Array.isArray(data?.templates) ? data.templates : [];
        setTemplates(list.filter((t) => String(t.status).toUpperCase() === "APPROVED"));
      })
      .catch((err) => {
        if (active) toastError(err);
      })
      .finally(() => {
        if (active) setLoadingTemplates(false);
      });
    return () => {
      active = false;
    };
  }, [open, official, whatsappId]);

  const total = alert?.affectedCount != null ? toNumber(alert.affectedCount) : null;
  const testDigits = testNumber.replace(/\D/g, "");
  const isTest = testDigits.length > 0;
  const testValid = testDigits.length >= 10 && testDigits.length <= 13;
  const canSend =
    !!whatsappId &&
    (acknowledged || (isTest && testValid)) &&
    !(isTest && !testValid) &&
    !sending &&
    (official ? !!templateId : !!text.trim());

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const body = { whatsappId: Number(whatsappId) };
      if (official) body.quickMessageId = Number(templateId);
      else body.text = text.trim();
      if (isTest) body.testNumber = testDigits;
      const { data } = await api.post(`/network-alerts/${alert.id}/broadcast`, body);
      const res = { campaignId: data?.campaignId, total: toNumber(data?.total) };
      setResult(res);
      toast.success(
        isTest
          ? i18n.t("networkAlerts.toasts.testSent", { number: testDigits })
          : i18n.t("networkAlerts.toasts.broadcastStarted", { total: res.total })
      );
      if (onDone) onDone(res);
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  const acknowledgeLabel =
    total != null
      ? i18n.t("networkAlerts.broadcast.acknowledge", { count: total })
      : i18n.t("networkAlerts.broadcast.acknowledgeUnknown");

  const requiredMark = (
    <span className={classes.required} aria-hidden="true">
      *
    </span>
  );
  const placeholder = <span className={classes.placeholder}>Selecione…</span>;
  const selectedTemplate = templates.find((t) => String(t.id) === templateId);
  const renderConnection = (w) => (
    <span className={classes.connectionOption}>
      <WhatsAppIcon />
      <span>{w.name}</span>
      {isOfficialConnection(w) && (
        <StatusBadge
          color="info"
          size="sm"
          icon={<VerifiedUserOutlinedIcon style={{ fontSize: 12 }} />}
          label={i18n.t("networkAlerts.broadcast.official")}
        />
      )}
    </span>
  );

  return (
    <Dialog
      open={open}
      onClose={() => !sending && onClose()}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      aria-labelledby="network-alert-broadcast-title"
    >
      <DialogTitle id="network-alert-broadcast-title">
        {i18n.t("networkAlerts.broadcast.title")}
        {alert && (
          <div className={classes.dialogSubtitle}>
            {alert.title} · {scopeLabel(alert.scopeType, alert.scopeValue)}
          </div>
        )}
      </DialogTitle>

      <DialogContent>
        {result ? (
          <div className={classes.result}>
            <CheckCircleOutlineIcon />
            <div className={classes.resultTitle}>{i18n.t("networkAlerts.broadcast.resultTitle")}</div>
            <div className={classes.resultText}>
              {i18n.t("networkAlerts.broadcast.resultMessage", {
                id: result.campaignId ?? "—",
                total: result.total,
              })}
            </div>
          </div>
        ) : (
          <div className={classes.formColumn}>
            {whatsapps.length === 0 && (
              <div className={classes.warning} style={{ marginBottom: 0 }} role="alert">
                <WarningRoundedIcon />
                <span>{i18n.t("networkAlerts.broadcast.noConnections")}</span>
              </div>
            )}
            <div className={classes.field}>
              <label className={classes.label} id="network-alert-broadcast-whatsapp-label">
                {i18n.t("networkAlerts.broadcast.whatsapp")}
                {requiredMark}
              </label>
              <TextField
                select
                id="network-alert-broadcast-whatsapp"
                value={whatsappId}
                onChange={(e) => setWhatsappId(e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                required
                disabled={whatsapps.length === 0}
                SelectProps={{
                  labelId: "network-alert-broadcast-whatsapp-label",
                  displayEmpty: true,
                  renderValue: () => (selected ? renderConnection(selected) : placeholder),
                }}
              >
                {whatsapps.map((w) => (
                  <MenuItem key={w.id} value={String(w.id)}>
                    {renderConnection(w)}
                  </MenuItem>
                ))}
              </TextField>
            </div>

            {official ? (
              <>
                {!loadingTemplates && templates.length === 0 && (
                  <div className={classes.warning} style={{ marginBottom: 0 }} role="alert">
                    <WarningRoundedIcon />
                    <span>{i18n.t("networkAlerts.broadcast.noTemplates")}</span>
                  </div>
                )}
                <div className={classes.field}>
                  <label className={classes.label} id="network-alert-broadcast-template-label">
                    {i18n.t("networkAlerts.broadcast.template")}
                    {requiredMark}
                  </label>
                  <TextField
                    select
                    id="network-alert-broadcast-template"
                    value={templateId}
                    onChange={(e) => setTemplateId(e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                    required
                    disabled={loadingTemplates || templates.length === 0}
                    SelectProps={{
                      labelId: "network-alert-broadcast-template-label",
                      displayEmpty: true,
                      renderValue: () => (selectedTemplate ? selectedTemplate.name : placeholder),
                    }}
                  >
                    {templates.map((t) => (
                      <MenuItem key={t.id} value={String(t.id)}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  <span className={classes.help}>
                    {loadingTemplates
                      ? i18n.t("networkAlerts.broadcast.loadingTemplates")
                      : i18n.t("networkAlerts.broadcast.templateHelp")}
                  </span>
                </div>
              </>
            ) : (
              <div className={classes.field}>
                <label className={classes.label} htmlFor="network-alert-broadcast-text">
                  {i18n.t("networkAlerts.broadcast.text")}
                  {requiredMark}
                </label>
                <TextField
                  id="network-alert-broadcast-text"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  variant="outlined"
                  size="small"
                  fullWidth
                  required
                  multiline
                  minRows={4}
                  maxRows={8}
                />
                <span className={classes.help}>{i18n.t("networkAlerts.broadcast.textHelp")}</span>
              </div>
            )}

            <div className={classes.field}>
              <label className={classes.label} htmlFor="network-alert-broadcast-test-number">
                {i18n.t("networkAlerts.broadcast.testNumber")}
              </label>
              <TextField
                id="network-alert-broadcast-test-number"
                value={testNumber}
                onChange={(e) => setTestNumber(e.target.value)}
                placeholder="51999998888"
                variant="outlined"
                size="small"
                fullWidth
                disabled={sending}
                error={isTest && !testValid}
              />
              <span className={classes.help}>{i18n.t("networkAlerts.broadcast.testNumberHelp")}</span>
            </div>

            <div>
              <div className={classes.summary}>
                <span>{i18n.t("networkAlerts.broadcast.recipients")}</span>
                <span className={classes.summaryValue}>
                  {total != null ? total : i18n.t("networkAlerts.broadcast.recipientsUnknown")}
                </span>
              </div>

              <FormControlLabel
                className={classes.acknowledge}
                control={
                  <Checkbox
                    checked={acknowledged}
                    onChange={(e) => setAcknowledged(e.target.checked)}
                    color="primary"
                  />
                }
                label={acknowledgeLabel}
              />
            </div>
          </div>
        )}
      </DialogContent>

      <DialogActions>
        {result ? (
          <>
            <Button onClick={onClose}>{i18n.t("networkAlerts.buttons.done")}</Button>
            <Button
              component={RouterLink}
              to="/invoice-campaigns"
              variant="contained"
              color="primary"
              onClick={onClose}
            >
              {i18n.t("networkAlerts.buttons.viewCampaign")}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={sending}>
              {i18n.t("networkAlerts.buttons.cancel")}
            </Button>
            <Button
              onClick={handleSend}
              variant="contained"
              color="primary"
              className={classes.btnWrapper}
              startIcon={<SendOutlinedIcon />}
              disabled={!canSend}
            >
              {i18n.t("networkAlerts.buttons.send")}
              {sending && <CircularProgress size={20} className={classes.buttonProgress} />}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BroadcastModal;
